/**
 * Filtro de domínios — porte JS 1:1 de cerberus/app/utils/dns_validation.py.
 *
 * Mantém paridade com o backend (Hefesto/app/common/dns.py) pra que o que é
 * filtrado client-side bata com o que é filtrado server-side. Atualizar este
 * arquivo SEMPRE que o equivalente Python mudar.
 */

const IPV4_RE = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/;
const IPV6_RES = [
    /^[0-9a-fA-F]*:[0-9a-fA-F:]*$/,
    /^::/,
    /::$/,
    /::.*::/,
];
const LABEL_RE = /^[a-zA-Z0-9-]+$/;
const HAS_LETTER = /[a-zA-Z]/;
const DNS_KEYWORDS = new Set(['localhost', 'local', 'lan']);

const GOV_TLDS = ['.gov.br', '.mil.br', '.leg.br', '.jus.br'];
const GOV_PATTERNS = [
    /^prefeitura\..*\.br$/,
    /^camara\..*\.br$/,
    /^assembleia\..*\.br$/,
    /^tribunal\..*\.br$/,
    /^tcu\..*\.br$/,
    /^tse\..*\.br$/,
    /^tre\..*\.br$/,
    /^stf\..*\.br$/,
    /^stj\..*\.br$/,
    /^cnj\..*\.br$/,
];

// Domínios amplamente utilizados — não bloqueia, só avisa.
// Match por igualdade exata OU subdomínio (ex.: web.whatsapp.com → whatsapp.com).
// Edite esta lista conforme a política do cliente.
export const ESSENTIAL_DOMAINS = [
    // === Google ecosystem ===
    'google.com', 'google.com.br', 'gmail.com', 'youtube.com', 'youtu.be',
    'googleapis.com', 'gstatic.com', 'googleusercontent.com', 'googlevideo.com',
    'doubleclick.net', 'googlesyndication.com', 'googletagmanager.com',
    'meet.google.com', 'drive.google.com', 'docs.google.com', 'photos.google.com',
    'android.com', 'firebase.com', 'firebaseio.com', 'goo.gl',

    // === Meta / Facebook ===
    'facebook.com', 'fb.com', 'fbcdn.net', 'instagram.com', 'cdninstagram.com',
    'whatsapp.com', 'whatsapp.net', 'messenger.com', 'oculus.com', 'meta.com',

    // === Outras redes / mensageria ===
    'twitter.com', 'x.com', 't.co', 'twimg.com',
    'linkedin.com', 'licdn.com',
    'tiktok.com', 'bytedance.com', 'tiktokcdn.com',
    'pinterest.com', 'reddit.com', 'redditstatic.com',
    'snapchat.com', 'telegram.org', 'telegram.me', 't.me',
    'signal.org', 'discord.com', 'discordapp.com', 'discord.gg',
    'slack.com', 'mattermost.com',

    // === Microsoft ===
    'microsoft.com', 'msftconnecttest.com', 'msftncsi.com',
    'office.com', 'office365.com', 'outlook.com', 'outlook.live.com',
    'live.com', 'hotmail.com', 'msn.com', 'bing.com',
    'microsoftonline.com', 'azure.com', 'azureedge.net', 'windowsupdate.com',
    'sharepoint.com', 'onedrive.com', 'onedrive.live.com',
    'teams.microsoft.com', 'skype.com', 'xbox.com', 'xboxlive.com',
    'visualstudio.com', 'github.com', 'githubusercontent.com', 'github.io',

    // === Apple ===
    'apple.com', 'icloud.com', 'me.com', 'mzstatic.com',
    'itunes.apple.com', 'mac.com', 'apple-cloudkit.com',

    // === Amazon / AWS ===
    'amazon.com', 'amazon.com.br', 'amazonaws.com', 'awsstatic.com',
    'cloudfront.net', 'a2z.com', 'media-amazon.com',
    'audible.com', 'twitch.tv', 'twitchcdn.net', 'ttvnw.net',
    'primevideo.com', 'kindle.com',

    // === Streaming ===
    'netflix.com', 'nflxso.net', 'nflxext.com', 'nflxvideo.net',
    'disneyplus.com', 'bamgrid.com', 'dssott.com',
    'hbomax.com', 'max.com', 'paramount.com', 'paramountplus.com',
    'spotify.com', 'scdn.co', 'spotifycdn.com',
    'deezer.com', 'soundcloud.com', 'tidal.com', 'globoplay.com',

    // === Cloud / dev infra ===
    'cloudflare.com', 'cloudflare.net', 'cdnjs.cloudflare.com',
    'akamai.net', 'akamaiedge.net', 'akamaized.net', 'akamaitechnologies.com',
    'fastly.net', 'jsdelivr.net', 'unpkg.com',
    'gitlab.com', 'bitbucket.org', 'atlassian.com', 'jira.com', 'confluence.com',
    'gravatar.com', 'wordpress.com', 'wordpress.org', 'wp.com',
    'stackoverflow.com', 'stackexchange.com', 'serverfault.com',
    'docker.com', 'docker.io', 'npmjs.com', 'pypi.org', 'pythonhosted.org',
    'cloudflare-dns.com',

    // === Comércio / marketplaces ===
    'mercadolivre.com.br', 'mercadolibre.com', 'mlstatic.com', 'mercadopago.com',
    'mercadopago.com.br', 'mercadolivre.com',
    'shopee.com.br', 'shopee.com', 'shopeesz.com',
    'aliexpress.com', 'alibaba.com', 'taobao.com',
    'magazineluiza.com.br', 'magalu.com', 'casasbahia.com.br', 'extra.com.br',
    'pontofrio.com.br', 'submarino.com.br', 'americanas.com.br', 'b2w.io',
    'shoptime.com.br', 'kabum.com.br', 'pichau.com.br', 'terabyteshop.com.br',
    'amazon.com.br', 'mercadoshops.com',

    // === Bancos / fintechs BR ===
    'itau.com.br', 'itaucard.com.br', 'bradesco.com.br', 'santander.com.br',
    'nubank.com.br', 'inter.co', 'bancointer.com.br',
    'sicoob.com.br', 'sicredi.com.br', 'btgpactual.com',
    'banco-original.com.br', 'next.me', 'bancopan.com.br', 'safra.com.br',
    'c6bank.com.br', 'neon.com.br', 'will.bank',
    'mercadopago.com.br',

    // === Pagamentos ===
    'paypal.com', 'paypal.com.br', 'paypalobjects.com',
    'stripe.com', 'pagseguro.com.br', 'pagseguro.uol.com.br',
    'picpay.com', 'rendaextra.com',
    'cielo.com.br', 'rede.com.br', 'getnet.com.br',

    // === Investimento BR ===
    'xpinvestimentos.com.br', 'xpi.com.br', 'rico.com.vc',
    'modalmais.com.br', 'easynvest.com.br', 'avenue.us',
    'b3.com.br', 'investing.com',

    // === Mídia / notícias BR ===
    'globo.com', 'g1.globo.com', 'gshow.com.br', 'oglobo.globo.com',
    'uol.com.br', 'noticias.uol.com.br', 'folha.uol.com.br',
    'terra.com.br', 'r7.com', 'ig.com.br', 'metropoles.com',
    'estadao.com.br', 'veja.abril.com.br', 'cnnbrasil.com.br',
    'band.uol.com.br', 'sbt.com.br', 'record.com.br',

    // === Mídia internacional ===
    'bbc.com', 'cnn.com', 'nytimes.com', 'theguardian.com',
    'reuters.com', 'bloomberg.com', 'wsj.com',
    'wikipedia.org', 'wikimedia.org', 'wiktionary.org',

    // === Educação ===
    'duolingo.com', 'khanacademy.org', 'udemy.com', 'coursera.org',
    'edx.org', 'alura.com.br', 'rocketseat.com.br',

    // === Produtividade ===
    'notion.so', 'notion.com', 'evernote.com', 'todoist.com',
    'trello.com', 'asana.com', 'monday.com', 'clickup.com',
    'figma.com', 'canva.com', 'miro.com', 'lucidchart.com',
    'adobe.com', 'creativecloud.com', 'behance.net',
    'dropbox.com', 'box.com',

    // === Vídeo conferência ===
    'zoom.us', 'zoom.com', 'webex.com', 'gotomeeting.com',

    // === Delivery / mobilidade BR ===
    'ifood.com.br', 'ifood-static.com.br',
    'uber.com', 'uber-com.br', 'ubereats.com',
    '99app.com', '99taxis.com', 'rappi.com.br', 'rappi.com',
    'jamesdelivery.com.br',

    // === Saúde ===
    'drconsulta.com', 'unimed.coop.br', 'amil.com.br',
    'sulamericaseguros.com.br', 'sulamerica.com.br',
    'einstein.br', 'hospitalsiriolibanes.org.br',

    // === Cursos / livros ===
    'udacity.com', 'goodreads.com', 'amazon.com.br',
    'medium.com', 'dev.to',

    // === Anti-virus / segurança ===
    'kaspersky.com', 'avast.com', 'avg.com', 'norton.com',
    'mcafee.com', 'bitdefender.com', 'malwarebytes.com',

    // === DNS públicos / CDN essenciais ===
    'one.one.one.one', 'quad9.net', 'opendns.com',

    // === Outros amplamente usados ===
    'paypal.com', 'ebay.com', 'booking.com', 'airbnb.com.br', 'airbnb.com',
    'expedia.com', 'decolar.com', 'latam.com', 'gol.com.br', 'azul.com.br',
    'tam.com.br', 'voeazul.com.br',
    'correios.com.br', 'enotas.com.br',
];

export function normalizeDomain(domain) {
    let d = (domain || '').toLowerCase().trim();
    if (d.startsWith('www.')) d = d.slice(4);
    return d;
}

export function isValidDomainFormatRfcCompliant(domain) {
    let d = (domain || '').trim();
    if (!d) return false;
    if (/[\s\v\f]/.test(d)) return false;
    if (d.length > 253) return false;
    if (!d.includes('.')) return false;
    if (d.startsWith('.') || d.endsWith('.')) return false;
    if (d.includes('..')) return false;
    if (IPV4_RE.test(d)) return false;
    for (const re of IPV6_RES) if (re.test(d)) return false;

    const labels = d.split('.');
    if (labels.length < 2) return false;
    for (const label of labels) {
        if (!label) return false;
        if (label.length > 63) return false;
        if (label.startsWith('-') || label.endsWith('-')) return false;
        if (!LABEL_RE.test(label)) return false;
    }

    const tld = labels[labels.length - 1];
    if (tld.length < 2) return false;
    if (!/[a-zA-Z]/.test(tld)) return false;
    if (d.length < 4) return false;
    if (!HAS_LETTER.test(d)) return false;

    const lower = labels.map((l) => l.toLowerCase());
    for (const kw of DNS_KEYWORDS) if (lower.includes(kw)) return false;

    return true;
}

export function isGovernmentDomain(domain) {
    const d = (domain || '').toLowerCase();
    for (const tld of GOV_TLDS) if (d.endsWith(tld)) return true;
    for (const re of GOV_PATTERNS) if (re.test(d)) return true;
    return false;
}

export function isEssentialDomain(domain) {
    const d = (domain || '').toLowerCase();
    if (!d) return false;
    for (const e of ESSENTIAL_DOMAINS) {
        if (d === e || d.endsWith('.' + e)) return true;
    }
    return false;
}

/**
 * Extrai e classifica domínios de um texto.
 * Retorna {
 *   valid:     string[]   domínios prontos pra cadastrar
 *   rejected:  {domain, reason}[]   gov/inválido — NÃO entram (hard block)
 *   essential: string[]   subset de valid que casou com lista de essenciais — entra mas avisa
 * }
 */
export function extractDomainsFromText(text) {
    const valid = new Set();
    const essential = [];
    const rejected = [];
    const seenInvalid = new Set();
    const seenEssential = new Set();
    if (!text) return { valid: [], rejected, essential };

    const t = text.trim();
    if (!t) return { valid: [], rejected, essential };

    const parts = t.split(/[,\s\n\r\t]+/);
    for (let part of parts) {
        part = part.trim();
        if (!part) continue;
        const original = part;

        // Remove protocolos
        if (part.startsWith('mailto:')) {
            part = part.slice(7);
            const at = part.indexOf('@');
            if (at >= 0) part = part.slice(at + 1);
        } else if (/^(https?|ftp):\/\//.test(part)) {
            part = part.split('://')[1] || '';
            part = part.split('/')[0];
            part = part.split('?')[0];
            part = part.split('#')[0];
        }
        part = part.split('/')[0].split('?')[0].split('#')[0].trim();
        if (!part) continue;

        const normalized = normalizeDomain(part);

        if (!isValidDomainFormatRfcCompliant(normalized)) {
            if (!seenInvalid.has(normalized)) {
                seenInvalid.add(normalized);
                rejected.push({ domain: original, reason: 'invalid' });
            }
            continue;
        }
        if (isGovernmentDomain(normalized)) {
            if (!seenInvalid.has(normalized)) {
                seenInvalid.add(normalized);
                rejected.push({ domain: normalized, reason: 'government' });
            }
            continue;
        }
        valid.add(normalized);
        if (isEssentialDomain(normalized) && !seenEssential.has(normalized)) {
            seenEssential.add(normalized);
            essential.push(normalized);
        }
    }

    return { valid: Array.from(valid), rejected, essential };
}

export const REASON_LABELS = {
    invalid: 'formato inválido',
    government: 'domínio governamental',
};
